import Phaser from 'phaser';

/**
 * Makes a Phaser game render at the display's real pixel density.
 *
 * Phaser's `Scale.RESIZE` mode sizes the drawing buffer to the parent element's
 * size in *CSS* pixels and never consults `devicePixelRatio`:
 *
 *     this.canvas.width  = styleWidth;   // ScaleManager.updateScale(), RESIZE branch
 *     this.canvas.height = styleHeight;
 *
 * On a 2x display that renders the whole scene at half the physical resolution and
 * leaves the browser to stretch it, which softens the moving target, the background
 * and the on-canvas text alike. `zoom` is ignored in that branch, and `resize()` is
 * no help either: it calls `refresh()`, which calls `updateScale()`, which puts the
 * CSS-pixel size straight back.
 *
 * So the hook goes on `updateScale` itself — the one place that decides the sizes.
 * Everything downstream then works off our numbers without being aware of any of
 * this, because `refresh()` runs in this order:
 *
 *     updateScale()  ->  updateBounds()  ->  displayScale = baseSize / canvasBounds
 *                                        ->  emit(RESIZE, gameSize, baseSize, ...)
 *
 * `displayScale` therefore comes out as the pixel ratio, which is exactly the factor
 * Phaser's input system needs to map a CSS-pixel pointer position onto the buffer;
 * the WebGL renderer and the camera manager both resize themselves from `baseSize`
 * on the emitted event.
 *
 * Task geometry needs no changes either: sprite scale, movement speed and the centre
 * position are all derived from `scene.game.canvas.width`, so they scale with the
 * buffer and the target keeps its on-screen size and speed.
 */

/**
 * Beyond 2x the extra pixels stop being visible but still cost fill rate, and this
 * runs next to a live video call. Phones report 3-4.
 */
const MAX_PIXEL_RATIO = 2;

const PATCHED = Symbol('hiDpiCanvasPatched');

type PatchableScaleManager = Phaser.Scale.ScaleManager & { [PATCHED]?: true };

function pixelRatio(): number {
  const ratio = window.devicePixelRatio || 1;
  return Math.min(Math.max(ratio, 1), MAX_PIXEL_RATIO);
}

/**
 * Call once, right after `new Phaser.Game(...)`. Safe to call on a game that is
 * already patched, and a no-op when there is nothing to gain (ratio 1) or when the
 * assumptions above do not hold — a Phaser upgrade that reworks `updateScale` or the
 * scale mode simply leaves the previous behaviour in place rather than breaking.
 */
export function enableHiDpiCanvas(game: Phaser.Game | null | undefined): void {
  const scale = game?.scale as PatchableScaleManager | undefined;

  if (!scale || scale[PATCHED]) return;
  if (scale.scaleMode !== Phaser.Scale.RESIZE) return;
  if (typeof scale.updateScale !== 'function') return;
  if (pixelRatio() === 1) return;

  const updateScale = scale.updateScale.bind(scale);

  scale.updateScale = function hiDpiUpdateScale() {
    updateScale();

    // Re-read every time: the ratio changes when the window moves to another monitor.
    const ratio = pixelRatio();
    const cssWidth = scale.parentSize.width;
    const cssHeight = scale.parentSize.height;

    // Before the parent is laid out there is nothing to scale, and dividing the
    // canvas into a zero-sized buffer would take the renderer down with it.
    if (ratio === 1 || cssWidth <= 0 || cssHeight <= 0) return;

    const bufferWidth = Math.floor(cssWidth * ratio);
    const bufferHeight = Math.floor(cssHeight * ratio);

    scale.gameSize.setSize(bufferWidth, bufferHeight);
    scale.baseSize.setSize(bufferWidth, bufferHeight);

    scale.canvas.width = bufferWidth;
    scale.canvas.height = bufferHeight;

    // The buffer grew, so the element has to be pinned back to its layout size —
    // the RESIZE branch leaves the canvas unstyled and lets the attributes decide.
    scale.canvas.style.width = `${cssWidth}px`;
    scale.canvas.style.height = `${cssHeight}px`;
  };

  scale[PATCHED] = true;

  // If the game has already sized itself, redo it through the patch. When it has not
  // booted yet there is nothing to redo — boot runs `updateScale` itself and picks the
  // patch up then.
  if (scale.canvas) {
    scale.refresh();
  }
}
